// Ampliación de baterías (Diego, 27-oct-2026; caso Jorge Rodríguez, San Carlos: tenía 6 Fortress eFlex MAX y se le suman 2).
// Una sola sección con todo lo de este tipo de cotización: el sistema que se amplía, los COSTOS (el vendedor no teclea precios:
// el servidor los calcula con la regla de Diego, costo ÷ (1 − 30 % − comisión − imprevistos), hacia arriba a $100), el precio que
// sale y la hoja 2 opcional con una noche MEDIDA en el monitoreo del cliente («Con 8 baterías llegas al amanecer»).
// Se guarda como las demás secciones: ediciones del borrador (/piezas/…) cada una con su «antes», y el servidor recalcula y
// devuelve los avisos (los rojos que detienen el PDF van arriba, con «Ver el dato» que enfoca su campo aquí).
// Costo, comisión, imprevistos, utilidad, margen y la regla: SOLO el director (el servidor no se los manda a nadie más).
// Verificación de ventas del 27-oct: lo tecleado y sin guardar vive fuera de la pantalla (logica/pendientes.ts): cambiar de
// pestaña pregunta «Guardar / Descartar» y cerrar la página pregunta el navegador; nunca se pierde en silencio.
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { MapPin, OctagonAlert, RotateCcw, Save } from 'lucide-react';
import { ANTICIPO_AMPLIACION_PCT, ENTREGA_AMPLIACION_DIAS, MUNICIPIOS_SONORA, esSonora, fechaHermosillo, fechaHoraCorta, margenPiezas, municipioDeCiudad, municipioPorClave, nombreBateria, numero, precargaDeFicha, reservaDeFicha } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { navegar, ponerGuardia, usarRuta } from '../../ruteo.tsx';
import { usarUsuario } from '../../sesion.tsx';
import { Boton, CampoSelect, CampoTexto, Dialogo, ErrorEnLinea, Interruptor, Seccion, Segmentado, Vacio } from '../../componentes/ui.tsx';
import { ETIQUETA_SEMAFORO, dinero } from '../../logica/etiquetas.ts';
import { numeroDe } from '../../logica/cuestionario.ts';
import { reemplazo } from '../../logica/ediciones.ts';
import { usarVistaPrecios } from '../../logica/vista-precios.ts';
import { basePendiente, pendienteAmpliacion, ponerPendienteAmpliacion, usarPendienteAmpliacion } from '../../logica/pendientes.ts';
import {
  claveBateria,
  conOtraBateria,
  costoDeOtraBateria,
  diaSiguiente,
  edicionesPiezas,
  formaAmpliacion,
  kwhCorto,
  piezasDeForma,
  primerFaltante,
  rangoAmpliacion,
  textoBateria,
  textoMsi,
  textoRegla,
  textoReserva,
} from '../../logica/ampliacion.ts';
import type { BateriaVista, ErroresAmpliacion, FormaAmpliacion } from '../../logica/ampliacion.ts';
import { usarCotizacion, usarPuedeAqui } from './contexto.tsx';

/** Un campo con su llave (la misma ruta que traen los avisos: «Ver el dato» lo enfoca). */
function Campo({ c, children }: { c: string; children: ReactNode }) {
  return <div data-campo={c}>{children}</div>;
}

/** Enfoca el campo de un aviso (?campo=/piezas/…): el que tenga esa llave o, si no se ve, el de su contenedor más cercano. */
function enfocar(campo: string): boolean {
  let c = campo;
  for (;;) {
    const el = document.querySelector<HTMLElement>(`[data-campo="${c}"]`);
    if (el) {
      const control = el.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled])') ?? el.querySelector<HTMLElement>('input, select, textarea');
      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
      control?.focus({ preventScroll: true });
      return true;
    }
    const i = c.lastIndexOf('/');
    if (i <= 0) return false;
    c = c.slice(0, i);
  }
}

export function SeccionAmpliacion() {
  const { cot, ejecutar } = usarCotizacion();
  const puede = usarPuedeAqui();
  const { veCostos, usuario } = usarUsuario();
  const { vista, error: errorVista } = usarVistaPrecios();
  const { consulta } = usarRuta();
  const b = cot.borrador;
  const piezas = b.piezas ?? null;
  const calculo = b.calculo_piezas ?? null;
  const editable = puede('editar_cotizaciones');
  // Ampliación AJENA para quien no ve costos (verificación de ventas del 27-oct, vista.ts): los costos que tecleó su vendedor le
  // llegan en null y el servidor no le deja cambiarlos. Se dice y esos campos no se tocan (nunca se mandan).
  const costosAjenos = !veCostos && cot.vendedor_id !== usuario.id;
  const editaCostos = editable && !costosAjenos;
  // Anticipo y entrega FIJOS (Diego, 27-oct noche): solo quien ve costos los cambia; los demás los ven y nunca los mandan.
  const opForma = useMemo(() => ({ costosAjenos, condicionesFijas: !veCostos }), [costosAjenos, veCostos]);

  // Las baterías que se pueden sumar; la guardada se queda en la lista aunque la tabla ya no la traiga (no se cambia sola).
  const baterias: BateriaVista[] = useMemo(() => {
    const lista = vista?.baterias_ampliacion ?? [];
    const g = piezas?.bateria ?? null;
    if (g && !lista.some((x) => claveBateria(x) === claveBateria(g))) return [...lista, { ...g, costo_en_tabla: piezas?.costo_bateria?.origen === 'tabla' }];
    return lista;
  }, [vista, piezas]);
  const guardada = piezas?.bateria ? (baterias.find((x) => claveBateria(x) === claveBateria(piezas.bateria!)) ?? null) : null;
  const enTabla = vista ? (guardada?.costo_en_tabla ?? null) : null;

  // La forma: lo guardado, o lo tecleado y sin guardar (logica/pendientes.ts, por cotización, sobrevive a cambiar de pestaña).
  // Si lo guardado cambió (otra pestaña, otra persona, o lo que se acaba de guardar), lo tecleado sobre lo de antes ya no aplica:
  // la forma vuelve a lo guardado.
  const original = useMemo(() => formaAmpliacion(piezas, enTabla), [piezas, enTabla]);
  const pendiente = usarPendienteAmpliacion(cot.id, piezas);
  const forma = pendiente?.forma ?? original;
  const base = basePendiente(piezas);
  useEffect(() => {
    const p = pendienteAmpliacion(cot.id);
    if (p && p.base !== base) ponerPendienteAmpliacion(cot.id, null);
  }, [cot.id, base]);
  const [baseVista, setBaseVista] = useState(base);
  const [errores, setErrores] = useState<ErroresAmpliacion>({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Cambios sin guardar y la persona cambia de pestaña o sale de la cotización: a dónde iba (el diálogo pregunta).
  const [salida, setSalida] = useState<string | null>(null);
  if (base !== baseVista) {
    setBaseVista(base);
    setErrores({});
  }
  const sucio = JSON.stringify(forma) !== JSON.stringify(original);
  const listo = useMemo(() => piezasDeForma(forma, baterias, piezas, opForma), [forma, baterias, piezas, opForma]);
  const ediciones = listo.piezas ? edicionesPiezas(piezas, listo.piezas) : null;

  /** Cambia la forma: lo que queda igual a lo guardado deja de estar pendiente. */
  const setForma = (cambio: FormaAmpliacion | ((f: FormaAmpliacion) => FormaAmpliacion)) => {
    const actual = pendienteAmpliacion(cot.id)?.base === base ? pendienteAmpliacion(cot.id)!.forma : original;
    const nueva = typeof cambio === 'function' ? cambio(actual) : cambio;
    if (JSON.stringify(nueva) === JSON.stringify(original)) ponerPendienteAmpliacion(cot.id, null);
    else {
      const r = piezasDeForma(nueva, baterias, piezas, opForma);
      ponerPendienteAmpliacion(cot.id, { forma: nueva, base, cambios: r.piezas ? edicionesPiezas(piezas, r.piezas).length : 0 });
    }
  };

  /** Cambia un dato de la forma (y quita el error de ese campo). */
  const poner = <K extends keyof FormaAmpliacion>(k: K, v: FormaAmpliacion[K], campo?: string) => {
    setForma((f) => ({ ...f, [k]: v }));
    if (campo && errores[campo]) setErrores((e) => ({ ...e, [campo]: undefined }));
  };
  const err = (c: string) => errores[c] ?? null;

  // Cambios sin guardar: cambiar de pestaña o salir de la cotización DENTRO de la app pregunta «Guardar / Descartar» (dentro de
  // «Ampliación», «Ver el dato» y lo demás pasa). Cerrar o recargar la página lo pregunta el navegador (logica/pendientes.ts).
  useEffect(() => {
    if (!sucio || !editable) return;
    return ponerGuardia((destino) => {
      if (destino === `/cotizaciones/${cot.id}/ampliacion` || destino.startsWith(`/cotizaciones/${cot.id}/ampliacion?`)) return true;
      setSalida(destino);
      return false;
    });
  }, [sucio, editable, cot.id]);

  // «Ver el dato» de un aviso: ?campo=/piezas/… enfoca ese campo (el rojo de «falta…» lleva al primero que falta).
  const pedido = consulta.get('campo');
  useEffect(() => {
    if (!pedido) return;
    const campo = pedido === '/piezas' ? primerFaltante(piezas) : pedido;
    // La hoja 2 apagada no tiene sus campos en pantalla: se enfoca su interruptor.
    window.setTimeout(() => enfocar(campo), 0);
    navegar(`/cotizaciones/${cot.id}/ampliacion`, { reemplazar: true });
  }, [pedido, piezas, cot.id]);

  /** Guarda lo tecleado. true = guardado (o no había nada que guardar); false = hay datos que no sirven o el servidor no quiso. */
  async function guardar(): Promise<boolean> {
    const r = piezasDeForma(forma, baterias, piezas, opForma);
    setErrores(r.errores);
    if (!r.piezas) {
      const primero = Object.keys(r.errores)[0];
      if (primero) window.setTimeout(() => enfocar(primero), 0);
      return false;
    }
    const eds = edicionesPiezas(piezas, r.piezas);
    if (!eds.length) {
      ponerPendienteAmpliacion(cot.id, null);
      return true;
    }
    setGuardando(true);
    setError(null);
    try {
      // Cada cambio lleva su «antes» (lo que la persona vio) y el servidor lo revisa: si la cotización cambió en otra cosa, se
      // recarga y se guarda sola, sin perder lo tecleado; si cambió ESTE dato, lo dice.
      await ejecutar((c) => api.editarBorrador(c.id, c.revision, eds), { reintentar: true });
      ponerPendienteAmpliacion(cot.id, null);
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setGuardando(false);
    }
  }

  const descartar = () => {
    ponerPendienteAmpliacion(cot.id, null);
    setErrores({});
  };

  const bateriaForma = baterias.find((x) => claveBateria(x) === forma.bateria) ?? null;
  const rango = rangoAmpliacion(bateriaForma, numeroDe(forma.actuales), numeroDe(forma.nuevas));
  const avisosNoche = b.avisos.filter((a) => a.codigo === 'AMPLIACION_NOCHE' && a.nivel === 'rojo' && !a.resuelto);
  const avisoViaticos = b.avisos.find((a) => a.codigo === 'FALTAN_VIATICOS' && a.nivel === 'rojo' && !a.resuelto) ?? null;
  const incompleta = b.avisos.find((a) => a.codigo === 'AMPLIACION_INCOMPLETA' && a.nivel === 'rojo' && !a.resuelto) ?? null;
  const margen = margenPiezas(b);
  const semaforo = margen?.semaforo ?? cot.semaforo_margen ?? null;
  // Lo que se precarga de la ficha de la batería elegida: la reserva del fabricante (no se teclea) y de dónde salen las horas.
  const deFicha = bateriaForma ? precargaDeFicha(bateriaForma) : null;
  const ficha = deFicha ? { reserva: deFicha.reserva_fabricante_pct === null ? '' : String(deFicha.reserva_fabricante_pct), fuente: deFicha.fuente } : null;
  const cambios = ediciones?.length ?? 0;
  // El costo de proveedor guardado es de otra batería (de antes de amarrarlo): llega vacío y se pide el de esta.
  const costoDeOtra = sucio ? null : costoDeOtraBateria(piezas, piezas?.bateria ?? null);
  // La reserva guardada no es la de la ficha (se tecleaba antes del 27-oct): el servidor la marca en rojo; se corrige al guardar.
  const reservaFicha = bateriaForma ? reservaDeFicha(bateriaForma) : null;
  const reservaVieja = forma.conHoja2 && !!piezas?.hoja2 && forma.reserva !== (reservaFicha === null ? '' : String(reservaFicha));
  // Las fechas medidas no pueden ser futuras (el servidor las rechaza): el calendario no las ofrece.
  const hoy = fechaHermosillo();

  return (
    <div className="pila pila--grande">
      <Seccion titulo="Sistema que se amplía" descripcion="La batería que ya tiene y cuántas se suman (del mismo modelo, en paralelo).">
        <div className="hoja pila">
          <Campo c="/piezas/bateria">
            <CampoSelect
              etiqueta="Batería"
              value={forma.bateria}
              disabled={!editable || (!vista && !errorVista)}
              onChange={(e) => {
                const nueva = baterias.find((x) => claveBateria(x) === e.target.value);
                // el costo de la anterior nunca se queda: «de la tabla» si la nueva lo trae, si no vacío (o el guardado si se vuelve a ella)
                if (nueva) setForma((f) => conOtraBateria(f, baterias.find((x) => claveBateria(x) === f.bateria) ?? null, nueva, original));
                if (errores['/piezas/bateria']) setErrores((x) => ({ ...x, '/piezas/bateria': undefined }));
              }}
              opciones={[
                ...(forma.bateria ? [] : [{ valor: '', texto: vista ? 'Elige la batería…' : 'Cargando las baterías…' }]),
                ...baterias.map((x) => ({ valor: claveBateria(x), texto: textoBateria(x) })),
              ]}
              error={err('/piezas/bateria')}
            />
          </Campo>
          {errorVista ? <ErrorEnLinea error={errorVista} /> : null}
          <div className="rejilla-2">
            <Campo c="/piezas/actuales">
              <CampoTexto etiqueta="Cuántas tiene" inputMode="numeric" disabled={!editable} value={forma.actuales} onChange={(e) => poner('actuales', e.target.value, '/piezas/actuales')} error={err('/piezas/actuales')} />
            </Campo>
            <Campo c="/piezas/nuevas">
              <CampoTexto etiqueta="Cuántas suma" inputMode="numeric" disabled={!editable} value={forma.nuevas} onChange={(e) => poner('nuevas', e.target.value, '/piezas/nuevas')} error={err('/piezas/nuevas')} />
            </Campo>
          </div>
          {rango ? (
            <p className={rango.pasa ? 'texto-rojo' : 'total-linea'} role="status">
              De <strong>{kwhCorto(rango.hoy)}</strong> a <strong>{kwhCorto(rango.con)} kWh</strong> · {rango.total} de {rango.maximo} baterías en paralelo (máximo de la ficha)
              {rango.pasa ? ': pasa del máximo.' : ''}
            </p>
          ) : bateriaForma ? (
            <p className="nota-suave">Hasta {bateriaForma.modulos_max} baterías en paralelo según la ficha.</p>
          ) : null}
        </div>
      </Seccion>

      <Seccion titulo="Costos (sin IVA)" descripcion="Lo que le cuesta a Solarix. El precio al cliente NO se teclea: lo calcula el cotizador con la regla de Diego.">
        <div className="hoja pila">
          {costosAjenos && (
            <p className="aviso aviso--amarillo" role="note">
              Los costos los captura el vendedor de esta cotización (o Diego): tú no los ves ni los cambias. Lo demás de la ampliación sí.
            </p>
          )}
          <Campo c="/piezas/costo_bateria">
            <Segmentado<FormaAmpliacion['costoOrigen']>
              etiqueta="Costo de cada batería"
              valor={forma.costoOrigen}
              alCambiar={(v) => editaCostos && poner('costoOrigen', v, '/piezas/costo_bateria')}
              opciones={[
                {
                  valor: 'tabla',
                  texto: 'De la tabla de precios',
                  detalle: bateriaForma?.costo_en_tabla === false ? 'Esta batería no lo trae' : 'Lo pone el cotizador',
                  deshabilitada: !editaCostos || bateriaForma?.costo_en_tabla === false,
                },
                { valor: 'proveedor', texto: 'Del proveedor', detalle: 'Lo tecleas con su cotización', deshabilitada: !editaCostos },
              ]}
              columnas={2}
              error={err('/piezas/costo_bateria')}
            />
          </Campo>
          {forma.costoOrigen === 'proveedor' && (
            <div className="rejilla-2">
              <Campo c="/piezas/costo_bateria/sin_iva">
                <CampoTexto
                  etiqueta="Costo de cada batería"
                  inputMode="decimal"
                  sufijo="$ sin IVA"
                  disabled={!editaCostos}
                  value={forma.costoProveedor}
                  onChange={(e) => poner('costoProveedor', e.target.value, '/piezas/costo_bateria/sin_iva')}
                  error={err('/piezas/costo_bateria/sin_iva') ?? (costoDeOtra && piezas?.bateria ? `El costo guardado es de la ${costoDeOtra}: teclea el de la ${nombreBateria(piezas.bateria)}.` : null)}
                />
              </Campo>
              <Campo c="/piezas/costo_bateria/fuente">
                <CampoTexto
                  etiqueta="¿De dónde sale?"
                  maxLength={200}
                  placeholder="Ej. Infinity Energy S01401: USD 1,529.20 × 19.227"
                  disabled={!editaCostos}
                  value={forma.fuenteProveedor}
                  onChange={(e) => poner('fuenteProveedor', e.target.value, '/piezas/costo_bateria/fuente')}
                  ayuda="Obligatorio: proveedor y su cotización. Diego lo ve en la bitácora al generar el PDF."
                  error={err('/piezas/costo_bateria/fuente')}
                />
              </Campo>
            </div>
          )}
          {forma.costoOrigen === 'tabla' && bateriaForma?.costo_en_tabla && <p className="nota-suave">El costo de la tabla lo pone el cotizador (no se enseña aquí).</p>}
          <div className="rejilla-3">
            <Campo c="/piezas/costo_material_sin_iva">
              <CampoTexto etiqueta="Material eléctrico" inputMode="decimal" sufijo="$" disabled={!editaCostos} value={forma.material} onChange={(e) => poner('material', e.target.value, '/piezas/costo_material_sin_iva')} ayuda="0 = no lleva material (no sale su partida)." error={err('/piezas/costo_material_sin_iva')} />
            </Campo>
            <Campo c="/piezas/costo_instalacion_sin_iva">
              <CampoTexto etiqueta="Instalación" inputMode="decimal" sufijo="$" disabled={!editaCostos} value={forma.instalacion} onChange={(e) => poner('instalacion', e.target.value, '/piezas/costo_instalacion_sin_iva')} ayuda="La mano de obra." error={err('/piezas/costo_instalacion_sin_iva')} />
            </Campo>
            <Campo c="/piezas/costo_viaticos_sin_iva">
              <CampoTexto
                etiqueta="Viáticos y traslado"
                inputMode="decimal"
                sufijo="$"
                disabled={!editaCostos}
                value={forma.viaticos}
                onChange={(e) => poner('viaticos', e.target.value, '/piezas/costo_viaticos_sin_iva')}
                ayuda="Fuera de Hermosillo son obligatorios (pregúntale al equipo técnico). Van con la instalación."
                error={err('/piezas/costo_viaticos_sin_iva') ?? (avisoViaticos && !sucio && !costosAjenos ? `Obra en ${cot.ciudad}: sin viáticos no sale el PDF.` : null)}
              />
            </Campo>
          </div>
          {/* Verificación de código del 27-oct: una obra EN Hermosillo con una ciudad que el cotizador no reconoce («Fracc. …») quedaba
              pidiendo viáticos sin salida. Como en las solares, se elige el municipio de la obra (/parametros/generacion/municipio). */}
          <MunicipioObra visible={!!avisoViaticos || !!cot.borrador.parametros.generacion?.municipio} editable={editable} />
          <Campo c="/piezas/precio_sujeto_a_confirmacion">
            <Interruptor
              etiqueta="Precio sujeto a confirmación del proveedor"
              detalle={forma.sujeto ? 'La descripción del PDF se lo dice al cliente.' : 'Préndelo si el proveedor todavía no confirma el costo.'}
              marcado={forma.sujeto}
              disabled={!editable}
              alCambiar={(v) => poner('sujeto', v)}
            />
          </Campo>
          {/* Diego, 27-oct noche: anticipo y entrega son FIJOS; solo él (ver_costos) los cambia. Los demás los ven como dato. */}
          {!veCostos ? (
            <div className="campo" data-campo="/piezas/anticipo_pct">
              <span className="campo__etiqueta">Anticipo y entrega</span>
              <p className="dato-ficha" data-condiciones>
                Anticipo {numero(piezas?.anticipo_pct ?? ANTICIPO_AMPLIACION_PCT)} % · entrega de {piezas?.entrega_dias_habiles.min ?? ENTREGA_AMPLIACION_DIAS.min} a{' '}
                {piezas?.entrega_dias_habiles.max ?? ENTREGA_AMPLIACION_DIAS.max} días hábiles · si el cliente necesita otros, pídeselo a Diego
              </p>
            </div>
          ) : (
          <div className="rejilla-3">
            <Campo c="/piezas/anticipo_pct">
              <CampoTexto etiqueta="Anticipo" inputMode="numeric" sufijo="%" disabled={!editable} value={forma.anticipo} onChange={(e) => poner('anticipo', e.target.value, '/piezas/anticipo_pct')} error={err('/piezas/anticipo_pct')} />
            </Campo>
            <Campo c="/piezas/entrega_dias_habiles/min">
              <CampoTexto etiqueta="Entrega desde" inputMode="numeric" sufijo="días hábiles" disabled={!editable} value={forma.entregaMin} onChange={(e) => poner('entregaMin', e.target.value, '/piezas/entrega_dias_habiles/min')} error={err('/piezas/entrega_dias_habiles/min')} />
            </Campo>
            <Campo c="/piezas/entrega_dias_habiles/max">
              <CampoTexto etiqueta="Hasta" inputMode="numeric" sufijo="días hábiles" disabled={!editable} value={forma.entregaMax} onChange={(e) => poner('entregaMax', e.target.value, '/piezas/entrega_dias_habiles/max')} error={err('/piezas/entrega_dias_habiles/max')} />
            </Campo>
          </div>
          )}
        </div>
      </Seccion>

      <Seccion titulo="Precio que sale" descripcion={sucio ? 'Es el de lo guardado: guarda tus cambios para ver el nuevo.' : 'Lo calcula el cotizador con los costos guardados.'}>
        {!calculo ? (
          // lo que falta ya lo dice el rojo de arriba (con «Ver el dato»): aquí, solo qué pasa cuando esté
          <Vacio titulo="Sin precio todavía">{incompleta ? 'Captura lo que dice el aviso rojo de arriba: con eso el precio sale solo.' : 'Captura la batería, cuántas suma y los costos: el precio sale solo.'}</Vacio>
        ) : (
          <div className="hoja pila">
            <div className="opcion__precio">
              <p className="opcion__etiqueta">Contado con IVA</p>
              <p className="opcion__cifra">{dinero(calculo.contado_con_iva, false)}</p>
              <p className="opcion__subtotal">
                {dinero(calculo.contado_sin_iva, false)} + IVA {dinero(calculo.iva, false)}
              </p>
              {/* los 3 MSI son los del cálculo (msi_3): arriba de $500,000 de lista el PDF no los imprime y aquí tampoco se prometen */}
              <p className="opcion__lista">
                {textoMsi(calculo, true)} · anticipo {numero(piezas?.anticipo_pct ?? 0)} %
              </p>
            </div>
            <p className="nota-suave">
              <strong>En el PDF sale un solo precio, sin desglose:</strong> lo que incluye (baterías, material e instalación, con su garantía) y el total. El precio por partida es solo para ti.
            </p>
            <table className="tabla tabla--compacta">
              <thead>
                <tr>
                  <th scope="col">Partida</th>
                  <th scope="col" className="num">
                    Importe sin IVA
                  </th>
                </tr>
              </thead>
              <tbody>
                {calculo.partidas.map((x) => (
                  <tr key={x.id}>
                    <th scope="row">
                      <span className="tabla__concepto">{x.concepto}</span>
                      <span className="tabla__secundario">
                        {x.cantidad} {x.unidad} × {dinero(x.pu_sin_iva)} · garantía {x.garantia.meses % 12 ? `${x.garantia.meses} meses` : `${x.garantia.meses / 12} ${x.garantia.meses === 12 ? 'año' : 'años'}`} {x.garantia.quien === 'fabricante' ? 'del fabricante' : 'de Solarix'}
                      </span>
                      {veCostos && x.costo_unitario_sin_iva !== undefined && <span className="tabla__secundario">Costo {dinero(x.costo_unitario_sin_iva, false)} c/u</span>}
                    </th>
                    <td className="num">{dinero(x.importe_sin_iva)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Contado sin IVA</th>
                  <td className="num">{dinero(calculo.contado_sin_iva)}</td>
                </tr>
              </tfoot>
            </table>
            <p className="nota-suave">
              Así lo describe el PDF: «{calculo.descripcion}»
            </p>
            {/* Costo, comisión, imprevistos, utilidad, margen y la regla: SOLO Diego (el servidor no se los manda a nadie más). */}
            {veCostos && calculo.costo_total_sin_iva !== undefined && calculo.utilidad_sin_iva !== undefined && (
              <div className={`rentabilidad rentabilidad--${margen?.semaforo ?? 'verde'}`} aria-label="Rentabilidad (solo Diego)">
                <div className="rentabilidad__cabeza">
                  {semaforo && (
                    <span className={`semaforo semaforo--${semaforo}`} data-semaforo={semaforo}>
                      <span className="semaforo__punto" aria-hidden />
                      {ETIQUETA_SEMAFORO[semaforo]}
                    </span>
                  )}
                  <span className="rentabilidad__utilidad">{calculo.utilidad_sin_iva < 0 ? `Pierde ${dinero(-calculo.utilidad_sin_iva)}` : `Deja ${dinero(calculo.utilidad_sin_iva)}`}</span>
                </div>
                <dl className="opcion__datos" data-rentabilidad>
                  <div>
                    <dt>Margen después de comisión e imprevistos</dt>
                    <dd>{calculo.margen_pct !== undefined ? `${numero(calculo.margen_pct, 1)} %` : '—'}</dd>
                  </div>
                  <div>
                    <dt>Utilidad</dt>
                    <dd>{dinero(calculo.utilidad_sin_iva)} sin IVA</dd>
                  </div>
                  <div>
                    <dt>Costo total</dt>
                    <dd>{dinero(calculo.costo_total_sin_iva, false)} sin IVA</dd>
                  </div>
                  <div>
                    <dt>Comisión · imprevistos</dt>
                    <dd>
                      {dinero(calculo.comision_sin_iva ?? 0)} · {dinero(calculo.imprevistos_sin_iva ?? 0)}
                    </dd>
                  </div>
                  {calculo.regla && (
                    <div className="opcion__dato--ancho">
                      <dt>Regla del precio (cada partida, hacia arriba a $100)</dt>
                      <dd data-regla>{textoRegla(calculo.regla)}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
            {!veCostos && semaforo && (
              <p className="rentabilidad__cabeza">
                <span className={`semaforo semaforo--${semaforo}`} data-semaforo={semaforo}>
                  <span className="semaforo__punto" aria-hidden />
                  {ETIQUETA_SEMAFORO[semaforo]}
                </span>
              </p>
            )}
          </div>
        )}
      </Seccion>

      <Seccion titulo="Hoja 2: cuánto le ayudan" descripcion="Opcional. Una hoja más que explica, con una noche MEDIDA en su monitoreo, hasta qué hora le alcanzaba y hasta cuál le alcanza con las nuevas.">
        <div className="hoja pila">
          <Campo c="/piezas/hoja2">
            <Interruptor
              etiqueta="Explicar cuánto le ayudan (con su monitoreo)"
              detalle={forma.conHoja2 ? 'Captura la noche en que el banco llegó al corte (la que se quedó sin luz).' : 'Sin ella sale solo la hoja 1 con lo que incluye y el precio.'}
              marcado={forma.conHoja2}
              disabled={!editable}
              alCambiar={(v) => {
                // Al prenderla, la reserva del fabricante es la de la ficha (no se teclea) y la fuente se precarga si está vacía.
                setForma((f) => ({ ...f, conHoja2: v, ...(v && ficha ? { reserva: ficha.reserva, fuente: f.fuente || ficha.fuente } : {}) }));
              }}
            />
          </Campo>
          {avisosNoche.map((a) => (
            <div key={a.id} className="aviso aviso--rojo" role="alert">
              <OctagonAlert className="aviso__icono" aria-hidden />
              <div className="aviso__cuerpo">
                <p className="aviso__mensaje">{a.mensaje}</p>
                <p className="aviso__meta">
                  <span>Sin arreglarlo no sale el PDF (o apaga la hoja 2).</span>
                </p>
              </div>
            </div>
          ))}
          {forma.conHoja2 && (
            <>
              <p className="grupo-respaldo__titulo">La noche medida</p>
              <div className="rejilla-2">
                <Campo c="/piezas/hoja2/noche/fecha">
                  <CampoTexto
                    etiqueta="Fecha (la tarde en que empezó)"
                    type="date"
                    max={hoy}
                    disabled={!editable}
                    value={forma.nocheFecha}
                    onChange={(e) => {
                      const v = e.target.value;
                      // con la recarga prendida y sin su fecha, se propone el día siguiente a la noche
                      setForma((f) => ({ ...f, nocheFecha: v, ...(f.conRecarga && !f.recargaFecha.trim() && diaSiguiente(v.trim()) ? { recargaFecha: diaSiguiente(v.trim())! } : {}) }));
                      if (errores['/piezas/hoja2/noche/fecha']) setErrores((x) => ({ ...x, '/piezas/hoja2/noche/fecha': undefined }));
                    }}
                    error={err('/piezas/hoja2/noche/fecha')}
                  />
                </Campo>
                <Campo c="/piezas/hoja2/noche/como">
                  <CampoTexto
                    etiqueta="¿Qué estaba prendido?"
                    maxLength={40}
                    placeholder="con los aires prendidos"
                    disabled={!editable}
                    value={forma.nocheComo}
                    onChange={(e) => poner('nocheComo', e.target.value, '/piezas/hoja2/noche/como')}
                    ayuda="Va a media frase: empieza con minúscula y sin punto final."
                    error={err('/piezas/hoja2/noche/como')}
                  />
                </Campo>
              </div>
              <div className="rejilla-2">
                <Campo c="/piezas/hoja2/noche/inicio">
                  <CampoTexto etiqueta="Empezó a bajar a las" inputMode="numeric" maxLength={5} placeholder="16:52" disabled={!editable} value={forma.nocheInicio} onChange={(e) => poner('nocheInicio', e.target.value, '/piezas/hoja2/noche/inicio')} ayuda="24 h, HH:MM." error={err('/piezas/hoja2/noche/inicio')} />
                </Campo>
                <Campo c="/piezas/hoja2/noche/inicio_pct">
                  <CampoTexto etiqueta="Con el banco a" inputMode="numeric" sufijo="%" placeholder="99" disabled={!editable} value={forma.nocheInicioPct} onChange={(e) => poner('nocheInicioPct', e.target.value, '/piezas/hoja2/noche/inicio_pct')} ayuda="Lleno: 95 % o más." error={err('/piezas/hoja2/noche/inicio_pct')} />
                </Campo>
                <Campo c="/piezas/hoja2/noche/corte">
                  <CampoTexto etiqueta="Se quedó sin luz a las" inputMode="numeric" maxLength={5} placeholder="04:33" disabled={!editable} value={forma.nocheCorte} onChange={(e) => poner('nocheCorte', e.target.value, '/piezas/hoja2/noche/corte')} ayuda="Cuando llegó al corte." error={err('/piezas/hoja2/noche/corte')} />
                </Campo>
                <Campo c="/piezas/hoja2/noche/sol">
                  <CampoTexto etiqueta="Los paneles cubren la casa a las" inputMode="numeric" maxLength={5} placeholder="07:40" disabled={!editable} value={forma.nocheSol} onChange={(e) => poner('nocheSol', e.target.value, '/piezas/hoja2/noche/sol')} ayuda="En la mañana." error={err('/piezas/hoja2/noche/sol')} />
                </Campo>
              </div>
              <div className="rejilla-3">
                <Campo c="/piezas/hoja2/corte_pct">
                  <CampoTexto etiqueta="Corte del inversor" inputMode="numeric" sufijo="%" placeholder="20" disabled={!editable} value={forma.corte} onChange={(e) => poner('corte', e.target.value, '/piezas/hoja2/corte_pct')} ayuda="Como está configurado hoy." error={err('/piezas/hoja2/corte_pct')} />
                </Campo>
                {/* La reserva del fabricante NO se teclea (verificación del 27-oct): «es la reserva que recomienda el fabricante» solo
                    con la del manual (RESERVAS_FABRICANTE); sin ella, la hoja no la menciona. Es un dato de la ficha, no un campo. */}
                <Campo c="/piezas/hoja2/reserva_fabricante_pct">
                  <div className="campo">
                    <span className="campo__etiqueta">Reserva del fabricante</span>
                    <p className="dato-ficha" data-reserva>
                      {textoReserva(bateriaForma)}
                    </p>
                    {reservaVieja ? (
                      <p className="campo__error">
                        Está guardada con {forma.reserva ? `${forma.reserva} %` : 'otra'} (tecleada antes): la que vale es la de la ficha.{' '}
                        {editable && (
                          <button type="button" className="enlace-boton" onClick={() => poner('reserva', reservaFicha === null ? '' : String(reservaFicha))}>
                            Usar la de la ficha
                          </button>
                        )}
                      </p>
                    ) : (
                      <p className="campo__ayuda">Sale de la ficha de la batería: no se teclea.</p>
                    )}
                  </div>
                </Campo>
                <Campo c="/piezas/hoja2/noche_normal_max_pct">
                  <CampoTexto etiqueta="Una noche normal usa menos de" inputMode="numeric" sufijo="%" placeholder="20" disabled={!editable} value={forma.normalMax} onChange={(e) => poner('normalMax', e.target.value, '/piezas/hoja2/noche_normal_max_pct')} ayuda="Del banco, sin lo de esa noche." error={err('/piezas/hoja2/noche_normal_max_pct')} />
                </Campo>
              </div>
              <Campo c="/piezas/hoja2/fuente">
                <CampoTexto
                  etiqueta="¿De dónde salen las horas?"
                  maxLength={60}
                  disabled={!editable}
                  value={forma.fuente}
                  onChange={(e) => poner('fuente', e.target.value, '/piezas/hoja2/fuente')}
                  ayuda={ficha && forma.fuente === ficha.fuente ? 'Precargado de la ficha. Va a media frase (minúscula, sin punto final).' : 'Va a media frase: minúscula y sin punto final.'}
                  error={err('/piezas/hoja2/fuente')}
                />
              </Campo>

              <Campo c="/piezas/hoja2/recarga">
                <Interruptor
                  etiqueta="La recarga medida (opcional)"
                  detalle={
                    forma.conRecarga
                      ? 'Es la del día SIGUIENTE a la noche (esa mañana el banco arranca del corte): a qué hora se llenó y con cuántos kW cargaba.'
                      : 'Sin ella la hoja NO dice «llegas al amanecer»: solo cuánto más te alcanza.'
                  }
                  marcado={forma.conRecarga}
                  disabled={!editable}
                  // Al prenderla se propone el día siguiente a la noche (la única recarga que dice cuánto tardan en llenarse las nuevas).
                  alCambiar={(v) => setForma((f) => ({ ...f, conRecarga: v, ...(v && !f.recargaFecha.trim() && diaSiguiente(f.nocheFecha.trim()) ? { recargaFecha: diaSiguiente(f.nocheFecha.trim())! } : {}) }))}
                />
              </Campo>
              {forma.conRecarga && (
                <div className="rejilla-3">
                  <Campo c="/piezas/hoja2/recarga/fecha">
                    <CampoTexto etiqueta="Fecha de la recarga" type="date" max={hoy} ayuda="El día siguiente a la noche." disabled={!editable} value={forma.recargaFecha} onChange={(e) => poner('recargaFecha', e.target.value, '/piezas/hoja2/recarga/fecha')} error={err('/piezas/hoja2/recarga/fecha')} />
                  </Campo>
                  <Campo c="/piezas/hoja2/recarga/llenas_a_las">
                    <CampoTexto etiqueta="Llenas a las" inputMode="numeric" maxLength={5} placeholder="13:33" disabled={!editable} value={forma.recargaLlenas} onChange={(e) => poner('recargaLlenas', e.target.value, '/piezas/hoja2/recarga/llenas_a_las')} ayuda="24 h, HH:MM." error={err('/piezas/hoja2/recarga/llenas_a_las')} />
                  </Campo>
                  <Campo c="/piezas/hoja2/recarga/carga_kw">
                    <CampoTexto etiqueta="Cargaban a" inputMode="decimal" sufijo="kW" placeholder="6.1" disabled={!editable} value={forma.recargaKw} onChange={(e) => poner('recargaKw', e.target.value, '/piezas/hoja2/recarga/carga_kw')} ayuda="La última hora antes de llenarse." error={err('/piezas/hoja2/recarga/carga_kw')} />
                  </Campo>
                </div>
              )}

              <Campo c="/piezas/hoja2/paneles">
                <Interruptor
                  etiqueta="Los paneles que ya tiene (opcional)"
                  detalle="Salen en la fila «tu sistema» de la hoja 2."
                  marcado={forma.conPaneles}
                  disabled={!editable}
                  alCambiar={(v) => poner('conPaneles', v)}
                />
              </Campo>
              {forma.conPaneles && (
                <div className="rejilla-2">
                  <Campo c="/piezas/hoja2/paneles/cantidad">
                    <CampoTexto etiqueta="Paneles" inputMode="numeric" placeholder="20" disabled={!editable} value={forma.panelesCantidad} onChange={(e) => poner('panelesCantidad', e.target.value, '/piezas/hoja2/paneles/cantidad')} error={err('/piezas/hoja2/paneles/cantidad')} />
                  </Campo>
                  <Campo c="/piezas/hoja2/paneles/w">
                    <CampoTexto etiqueta="Watts de cada panel" inputMode="numeric" sufijo="W" placeholder="540" disabled={!editable} value={forma.panelesW} onChange={(e) => poner('panelesW', e.target.value, '/piezas/hoja2/paneles/w')} error={err('/piezas/hoja2/paneles/w')} />
                  </Campo>
                </div>
              )}
            </>
          )}
        </div>
      </Seccion>

      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      {Object.values(errores).some(Boolean) && <p className="campo__error">Hay datos que no sirven (marcados en rojo): corrígelos para guardar.</p>}
      {editable && sucio && (
        <div className="barra-guardar" role="region" aria-label="Cambios sin guardar">
          <span>
            {cambios === 1 ? '1 cambio sin guardar' : cambios > 1 ? `${cambios} cambios sin guardar` : 'Cambios sin guardar'}
            <span className="barra-guardar__detalle"> · al guardar se recalcula el precio</span>
          </span>
          <div className="fila-botones">
            <Boton chico variante="fantasma" icono={<RotateCcw aria-hidden />} onClick={descartar}>
              Descartar
            </Boton>
            <Boton chico variante="principal" icono={<Save aria-hidden />} cargando={guardando} onClick={() => void guardar()}>
              Guardar
            </Boton>
          </div>
        </div>
      )}
      <p className="nota-suave">Última actualización: {fechaHoraCorta(cot.actualizada_en)}</p>
      <Dialogo
        abierto={salida !== null}
        alCerrar={() => setSalida(null)}
        titulo="Cambios sin guardar"
        pie={
          <div className="fila-botones fila-botones--fin">
            <Boton
              variante="fantasma"
              icono={<RotateCcw aria-hidden />}
              onClick={() => {
                const a = salida;
                descartar();
                setSalida(null);
                if (a) navegar(a, { forzar: true });
              }}
            >
              Descartar
            </Boton>
            <Boton
              variante="principal"
              icono={<Save aria-hidden />}
              cargando={guardando}
              onClick={async () => {
                const a = salida;
                const ok = await guardar();
                setSalida(null);
                if (ok && a) navegar(a, { forzar: true });
              }}
            >
              Guardar
            </Boton>
          </div>
        }
      >
        <p>
          {cambios === 1 ? 'Tienes 1 cambio sin guardar' : cambios > 1 ? `Tienes ${cambios} cambios sin guardar` : 'Tienes cambios sin guardar'} en «Ampliación». ¿Los guardas antes de salir? Si los
          descartas, se pierden.
        </p>
      </Dialogo>
    </div>
  );
}

/** El municipio de la obra cuando la ciudad del cuestionario no se reconoce (como en las solares, Generacion.tsx): con él el
 *  servidor decide si la obra es fuera de Hermosillo (viáticos). Manda /parametros/generacion/municipio con su «antes» (null si
 *  la cotización todavía no tiene `generacion`: el servidor la arma con la base de Diego y ese municipio). */
function MunicipioObra({ visible, editable }: { visible: boolean; editable: boolean }) {
  const { cot, ejecutar } = usarCotizacion();
  const q = cot.borrador.cuestionario;
  const g = cot.borrador.parametros.generacion ?? null;
  const elegido = g?.municipio ?? '';
  const [abierto, setAbierto] = useState(false);
  const [municipio, setMunicipio] = useState(elegido);
  const [mal, setMal] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Solo sirve si la ciudad NO se reconoce: con una ciudad reconocida manda la ciudad (municipioDeLaObra del motor).
  if (!visible || !esSonora(q.cliente.estado) || municipioDeCiudad(q.cliente.ciudad)) return null;
  const actual = municipioPorClave(elegido || null);

  async function guardarMunicipio() {
    if (!municipio) {
      setMal('Elige el municipio de la obra.');
      return;
    }
    setOcupado(true);
    setError(null);
    try {
      await ejecutar((c) => api.editarBorrador(c.id, c.revision, [reemplazo('/parametros/generacion/municipio', c.borrador.parametros.generacion?.municipio ?? null, municipio, 'Municipio de la obra')]), {
        reintentar: true,
      });
      setAbierto(false);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="pila pila--chica" data-campo="/parametros/generacion/municipio">
      {!abierto ? (
        <>
          <p className="nota-suave">
            {actual ? (
              <>
                Obra en el municipio de <strong>{actual.municipio}</strong> (elegido: la ciudad «{q.cliente.ciudad}» no se reconoce).
              </>
            ) : (
              <>¿La obra es en Hermosillo y la ciudad «{q.cliente.ciudad}» no se reconoce? Elige el municipio de la obra: con él se sabe si lleva viáticos.</>
            )}
          </p>
          {editable && (
            <div className="fila-botones">
              <Boton chico variante="secundario" icono={<MapPin aria-hidden />} onClick={() => setAbierto(true)}>
                {actual ? 'Cambiar el municipio de la obra' : 'Elegir el municipio de la obra'}
              </Boton>
            </div>
          )}
        </>
      ) : (
        <>
          <CampoSelect
            etiqueta="Municipio de la obra"
            value={municipio}
            disabled={ocupado}
            error={mal}
            ayuda={`La ciudad del cuestionario («${q.cliente.ciudad}») no es un municipio que el cotizador reconozca.`}
            onChange={(e) => {
              setMunicipio(e.target.value);
              setMal(null);
            }}
            opciones={[{ valor: '', texto: 'Elige el municipio…' }, ...MUNICIPIOS_SONORA.map((x) => ({ valor: x.clave, texto: x.nombre }))]}
          />
          <div className="fila-botones">
            <Boton chico variante="fantasma" disabled={ocupado} onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
            <Boton chico variante="principal" cargando={ocupado} onClick={() => void guardarMunicipio()}>
              Guardar el municipio
            </Boton>
          </div>
        </>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </div>
  );
}
